import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Package, Scan, Plus, Trash2, ArrowLeft, ShieldAlert, CheckCircle,
  AlertTriangle, Loader2, Search, X, Save
} from 'lucide-react';
import { useLocalDraft } from '../utils/draftPersist';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GST_RATE = 0.15;
const GST_MULT = 1 + GST_RATE;
// Prices in this app: `buy_price` (aka unit_cost) is EX-GST — what we pay the
// supplier before tax. `sell_price` is the shelf/retail price INC-GST — what
// the consumer pays. Margin is calculated on an apples-to-apples ex-GST basis
// or the number is inflated by ~8 percentage points.
//
// Min 42% margin (real) →  profit / sell_ex >= 0.42  →  sell_ex >= cost / 0.58
//                       →  sell_inc >= cost / 0.58 × 1.15  ≈  cost × 1.9828
const MIN_MARGIN = 0.42;
const MIN_SELL_MULTIPLIER = GST_MULT / (1 - MIN_MARGIN); // ≈ 1.9828 (inc-GST retail floor)
const VARIANCE_TOLERANCE = 0.50;

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
// Cost is ex-GST, sell is inc-GST — strip GST off sell before computing
// so the displayed margin matches what you actually pocket after paying GST.
const marginPct = (cost, sell) => {
  if (!sell || sell <= 0) return 0;
  const sellExGst = sell / GST_MULT;
  return sellExGst > 0 ? ((sellExGst - cost) / sellExGst) * 100 : 0;
};

const ReceiveStock = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedStore, storeName } = useStore();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Step: 'setup' (header form) → 'receive' (line-by-line)
  const [step, setStep, stepDraft] = useLocalDraft('receive_stock_step', 'setup');
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Setup form — auto-saved to localStorage so it survives navigation
  const [setupForm, setSetupForm, setupDraft] = useLocalDraft('receive_stock_setup', {
    supplier_id: '',
    supplier_name: '',
    invoice_number: '',
    received_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    payment_status: 'unpaid',
    store: selectedStore || 'orewa',
    gst_amount: '',
    freight: '',
    cc_surcharge: '',
    total_amount: '',
  });

  // Receive screen
  const [scanInput, setScanInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // Line items — auto-saved to localStorage
  const [lines, setLines, linesDraft] = useLocalDraft('receive_stock_lines', []);
  const scanRef = useRef(null);

  // Override dialog state
  const [overrideDialog, setOverrideDialog] = useState(null); // { lineKey, pin:'', reason:'', verifying }
  // Quick-add product dialog (unknown barcode)
  const [addProductDialog, setAddProductDialog] = useState(null); // { barcode, name, brand, supplier_id, supplier_name, supplier_code, tax_rate, buy_price, sell_price, category, publish_to_shopify }

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/suppliers`, auth),
      // Fetch the full catalogue so universal cross-supplier search actually
      // works. Previous 10k cap silently dropped ~1k products (alphabetically
      // last — Weleda + Zeally + Z-brands), making cross-supplier receiving
      // impossible for the exact SKUs the user reported. 50k covers 5× the
      // current catalogue with headroom; server response is still <2s.
      axios.get(`${API_URL}/products?limit=50000`, auth),
    ]).then(([s, p]) => {
      setSuppliers((s.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
      setProducts(p.data || []);
    }).catch(() => toast.error('Failed to load setup data'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus scan input on receive step
  useEffect(() => {
    if (step === 'receive' && scanRef.current) {
      setTimeout(() => scanRef.current?.focus(), 100);
    }
  }, [step]);

  const selectedSupplierId = setupForm.supplier_id;

  // ---------- Step 1: Setup ----------

  const handleStartReceive = () => {
    if (!setupForm.supplier_id) return toast.error('Pick a supplier');
    const total = parseFloat(setupForm.total_amount);
    const gst = parseFloat(setupForm.gst_amount);
    if (!(total > 0)) return toast.error('Enter the total invoice amount');
    if (!(gst >= 0)) return toast.error('Enter the GST amount');
    if (gst >= total) return toast.error('GST cannot be >= total');
    setStep('receive');
  };

  // ---------- Step 2: Receive lines ----------

  const addLineForProduct = (product, qty = 1) => {
    const key = `${product.id}-${Date.now()}`;
    const cost = Number(product.buy_price || 0);
    const existingSell = Number(product.sell_price || 0);
    setLines(prev => [...prev, {
      key,
      product_id: product.id,
      name: product.name,
      barcode: product.barcode || '',
      quantity: qty,
      unit_cost: cost,
      sell_price: existingSell,
      existing_sell: existingSell,
      markup_override: false,
      override_reason: '',
      override_old_sell: existingSell,
    }]);
  };

  const handleScan = (raw) => {
    const code = (raw || '').trim();
    if (!code) return;
    const match = products.find(p => (p.barcode || '').trim() === code);
    if (match) {
      // If already on the list, bump qty by 1
      const existingIdx = lines.findIndex(l => l.product_id === match.id);
      if (existingIdx >= 0) {
        setLines(prev => prev.map((l, i) => i === existingIdx ? { ...l, quantity: l.quantity + 1 } : l));
      } else {
        addLineForProduct(match, 1);
      }
      toast.success(`+ ${match.name}`);
    } else {
      // Unknown barcode → open Add Product dialog pre-filled
      setAddProductDialog({
        barcode: code,
        name: '',
        brand: '',
        supplier_id: setupForm.supplier_id,
        supplier_name: setupForm.supplier_name,
        supplier_code: '',
        tax_rate: 15,
        category: 'General',
        buy_price: '',
        sell_price: '',
        stock_qty: 1,
        publish_to_shopify: false,
        creating: false,
      });
    }
    setScanInput('');
  };

  const updateLine = (key, patch) => {
    setLines(prev => prev.map(l => l.key === key ? { ...l, ...patch } : l));
  };

  const removeLine = (key) => setLines(prev => prev.filter(l => l.key !== key));

  // ---------- Quick Add Product ----------

  const handleQuickAddProduct = async () => {
    const a = addProductDialog;
    if (!a.name.trim()) return toast.error('Product name required');
    const cost = parseFloat(a.buy_price);
    const sell = parseFloat(a.sell_price);
    if (!(cost >= 0)) return toast.error('Cost price required');
    if (!(sell >= 0)) return toast.error('Retail price required');
    setAddProductDialog({ ...a, creating: true });
    try {
      const payload = {
        name: a.name.trim(),
        barcode: a.barcode || '',
        supplier_code: a.supplier_code || '',
        brand: a.brand || '',
        supplier_id: a.supplier_id || null,
        supplier_name: a.supplier_name || '',
        tax_rate: parseFloat(a.tax_rate) || 15,
        category: a.category || 'General',
        buy_price: cost,
        sell_price: sell,
        quantity: 0,
        stock_orewa: 0,
        stock_eastridge: 0,
      };
      const res = await axios.post(`${API_URL}/products`, payload, auth);
      const newProduct = res.data;

      // Optionally push to Shopify (same endpoint as Inventory page)
      if (a.publish_to_shopify) {
        try {
          await axios.post(
            `${API_URL}/shopify/create-product?product_id=${newProduct.id}`,
            {},
            auth
          );
          toast.success('Published to Shopify');
        } catch (shopErr) {
          toast.error('Product created but Shopify publish failed: ' + (shopErr.response?.data?.detail || 'unknown'));
        }
      }

      setProducts(prev => [...prev, newProduct]);
      addLineForProduct(newProduct, parseInt(a.stock_qty) || 1);
      setAddProductDialog(null);
      toast.success(`Added ${newProduct.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create product');
      setAddProductDialog({ ...a, creating: false });
    }
  };

  // ---------- Override flow ----------

  const openOverrideDialog = (lineKey) => {
    setOverrideDialog({ lineKey, pin: '', reason: '', verifying: false });
  };

  const submitOverride = async () => {
    const o = overrideDialog;
    if (!o.pin.trim()) return toast.error('Manager PIN required');
    if (!o.reason.trim()) return toast.error('Reason required');
    setOverrideDialog({ ...o, verifying: true });
    try {
      const res = await axios.post(`${API_URL}/stock-receipts/verify-pin`, { pin: o.pin }, auth);
      if (!res.data.valid) {
        setOverrideDialog({ ...o, verifying: false });
        return toast.error('Invalid manager PIN');
      }
      const line = lines.find(l => l.key === o.lineKey);
      updateLine(o.lineKey, {
        markup_override: true,
        override_reason: o.reason.trim(),
        override_old_sell: line?.existing_sell ?? line?.sell_price,
      });
      setOverrideDialog(null);
      toast.success('Override approved & logged to Staff Activity');
    } catch {
      setOverrideDialog({ ...o, verifying: false });
      toast.error('Verification failed');
    }
  };

  // ---------- Totals & balance ----------

  const lineNet = useMemo(() => lines.reduce((s, l) => s + (l.quantity * l.unit_cost), 0), [lines]);
  const invoiceTotal = parseFloat(setupForm.total_amount) || 0;
  const enteredGst = parseFloat(setupForm.gst_amount) || 0;
  const freight = parseFloat(setupForm.freight) || 0;
  const ccSurcharge = parseFloat(setupForm.cc_surcharge) || 0;
  const runningTotal = lineNet + enteredGst + freight + ccSurcharge;
  const remaining = invoiceTotal - runningTotal; // positive = still need to scan more
  const balanced = Math.abs(remaining) <= VARIANCE_TOLERANCE && lines.length > 0;

  // Classic "out by exactly the GST" pattern: unit costs were typed GST-INCLUSIVE,
  // so lines + GST double-counts. One click strips GST from every line.
  const overByExactlyGst = enteredGst > 0 && lines.length > 0 && Math.abs((-remaining) - enteredGst) <= 0.50;
  const convertLinesToExGst = () => {
    setLines(prev => prev.map(l => ({ ...l, unit_cost: +(l.unit_cost / 1.15).toFixed(2) })));
    toast.success('Line costs converted to ex-GST (÷1.15) — balance updated');
  };

  const linesUnderMarkup = lines.filter(l => {
    if (l.unit_cost <= 0) return false;
    if (l.markup_override) return false;
    return l.sell_price + 0.005 < l.unit_cost * MIN_SELL_MULTIPLIER;
  });

  // NEW behaviour (Feb 2026): under-margin lines WARN but no longer BLOCK.
  // User can accept the whole receipt with a single confirmation, or fix the
  // retail prices, or use per-line override to log a specific reason. This
  // replaces the old "must override each row" flow which was frustrating on
  // large receipts (20+ Metagenics lines).
  const canAccept = balanced && !submitting;

  const handleAccept = async () => {
    if (!canAccept) return;
    // If any under-margin lines remain, ask once — a single confirmation for
    // the whole receipt. On confirm, every under-margin line without explicit
    // override gets auto-flagged with a generic reason so Staff Activity still
    // logs them.
    let autoOverrides = null;
    if (linesUnderMarkup.length > 0) {
      const summary = linesUnderMarkup.map(l => {
        const profit = ((l.sell_price / GST_MULT) - l.unit_cost) * l.quantity;
        return `  • ${l.name.slice(0, 44)} — ${marginPct(l.unit_cost, l.sell_price).toFixed(0)}% (\$${profit.toFixed(2)} profit / ${l.quantity}u)`;
      }).join('\n');
      const ok = window.confirm(
        `${linesUnderMarkup.length} line(s) below 42% margin. Accept anyway?\n\n` +
        summary +
        `\n\nAll under-margin lines will be logged to Staff Activity with reason ` +
        `"Accepted below 42% margin — no per-line override".`
      );
      if (!ok) return;
      // Build a map of lineKey → auto-override so we don't mutate state
      // (state is React, mutating causes trouble; do this at payload-build).
      autoOverrides = new Set(linesUnderMarkup.map(l => l.key));
    }

    setSubmitting(true);
    try {
      const payload = {
        supplier_id: setupForm.supplier_id,
        supplier_name: setupForm.supplier_name,
        invoice_number: setupForm.invoice_number,
        received_date: setupForm.received_date,
        due_date: setupForm.due_date || '',
        payment_status: setupForm.payment_status || 'unpaid',
        store: setupForm.store,
        gst_amount: enteredGst,
        freight: freight,
        cc_surcharge: ccSurcharge,
        total_amount: invoiceTotal,
        lines: lines.map(l => {
          const isAutoOverride = autoOverrides?.has(l.key);
          return {
            product_id: l.product_id,
            barcode: l.barcode,
            name: l.name,
            quantity: parseInt(l.quantity) || 0,
            unit_cost: Number(l.unit_cost) || 0,
            sell_price: Number(l.sell_price) || 0,
            markup_override: !!l.markup_override || !!isAutoOverride,
            override_reason: l.override_reason || (isAutoOverride
              ? 'Accepted below 42% margin — no per-line override'
              : ''),
            override_old_sell_price: l.override_old_sell ?? null,
          };
        }),
      };
      const res = await axios.post(`${API_URL}/stock-receipts`, payload, auth);
      toast.success(`Receipt accepted. Invoice created (${res.data.lines_count} items).`);
      // Clear draft on successful submit
      stepDraft.clearDraft();
      setupDraft.clearDraft();
      linesDraft.clearDraft();
      navigate('/invoices');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save receipt');
      setSubmitting(false);
    }
  };

  // ============================================================================
  // RENDER — STEP 1: Setup
  // ============================================================================
  // Auto-saved draft banner shared by both steps
  const hasAnyDraft = setupDraft.savedAt || linesDraft.savedAt;
  const DraftBanner = () => hasAnyDraft ? (
    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-sm">
      <div className="flex items-center gap-2 text-emerald-700">
        <Save className="w-4 h-4" />
        <span className="font-medium">Auto-saved draft</span>
        <span className="text-emerald-600 text-xs">
          ({lines.length} line{lines.length === 1 ? '' : 's'} · safe to leave & return)
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
        data-testid="discard-receive-draft"
        onClick={() => {
          if (!window.confirm('Discard the auto-saved draft? All entered lines and setup info will be cleared.')) return;
          stepDraft.clearDraft();
          setupDraft.clearDraft();
          linesDraft.clearDraft();
          window.location.reload();
        }}
      >
        Discard draft
      </Button>
    </div>
  ) : null;

  if (step === 'setup') {
    return (
      <div className="space-y-6 max-w-2xl" data-testid="receive-stock-setup">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/smart-po')} data-testid="back-to-smart-po">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Receive Stock</h1>
            <p className="text-slate-500 mt-1 text-sm">Receive supplier stock without creating a Smart PO</p>
          </div>
        </div>

        <DraftBanner />

        <Card className="border-2 border-orange-100">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>Supplier *</Label>
              <select
                value={setupForm.supplier_id}
                onChange={(e) => {
                  const s = suppliers.find(x => x.id === e.target.value);
                  setSetupForm({ ...setupForm, supplier_id: e.target.value, supplier_name: s?.name || '' });
                }}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-10"
                data-testid="rs-supplier-select"
              >
                <option value="">— Select supplier —</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invoice Number</Label>
                <Input
                  value={setupForm.invoice_number}
                  onChange={(e) => setSetupForm({ ...setupForm, invoice_number: e.target.value })}
                  placeholder="e.g. INV-2026-1234"
                  data-testid="rs-invoice-number"
                />
              </div>
              <div>
                <Label>Received Date</Label>
                <Input
                  type="date"
                  value={setupForm.received_date}
                  onChange={(e) => setSetupForm({ ...setupForm, received_date: e.target.value })}
                  data-testid="rs-received-date"
                />
              </div>
            </div>

            {/* Due Date + Paid/Unpaid → flows through to Invoices page */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={setupForm.due_date}
                  onChange={(e) => setSetupForm({ ...setupForm, due_date: e.target.value })}
                  data-testid="rs-due-date"
                />
              </div>
              <div>
                <Label>Payment Status</Label>
                <div className="flex border border-slate-300 rounded overflow-hidden h-10">
                  <button
                    type="button"
                    onClick={() => setSetupForm({ ...setupForm, payment_status: 'unpaid' })}
                    className={`flex-1 text-sm font-bold ${setupForm.payment_status === 'unpaid' ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid="rs-status-unpaid"
                  >Unpaid</button>
                  <button
                    type="button"
                    onClick={() => setSetupForm({ ...setupForm, payment_status: 'paid' })}
                    className={`flex-1 text-sm font-bold border-l border-slate-300 ${setupForm.payment_status === 'paid' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid="rs-status-paid"
                  >Paid</button>
                </div>
              </div>
            </div>

            <div>
              <Label>Store *</Label>
              <select
                value={setupForm.store}
                onChange={(e) => setSetupForm({ ...setupForm, store: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-10"
                data-testid="rs-store-select"
              >
                <option value="orewa">{storeName('orewa')}</option>
                <option value="eastridge">{storeName('eastridge')}</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GST Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={setupForm.gst_amount}
                  onChange={(e) => setSetupForm({ ...setupForm, gst_amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="rs-gst-amount"
                />
              </div>
              <div>
                <Label>Total Amount (GST inclusive) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={setupForm.total_amount}
                  onChange={(e) => setSetupForm({ ...setupForm, total_amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="rs-total-amount"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Freight</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={setupForm.freight}
                  onChange={(e) => setSetupForm({ ...setupForm, freight: e.target.value })}
                  placeholder="0.00"
                  data-testid="rs-freight"
                />
                <p className="text-[11px] text-slate-400 mt-1">Optional — shipping/delivery charge.</p>
              </div>
              <div>
                <Label>Credit Card Surcharge</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={setupForm.cc_surcharge}
                  onChange={(e) => setSetupForm({ ...setupForm, cc_surcharge: e.target.value })}
                  placeholder="0.00"
                  data-testid="rs-cc-surcharge"
                />
                <p className="text-[11px] text-slate-400 mt-1">Optional — card processing fee.</p>
              </div>
            </div>

            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
              Receipt must balance to invoice total within <b>$0.50</b>.
              Any line with retail under <b>42% margin</b> needs a manager PIN + reason (logged in Staff Activity).
            </div>

            <Button
              className="w-full bg-orange-500 hover:bg-orange-600 text-white h-11 text-base font-semibold"
              onClick={handleStartReceive}
              data-testid="rs-start-receiving"
            >
              Start Receiving →
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // RENDER — STEP 2: Receive
  // ============================================================================
  return (
    <div className="space-y-4" data-testid="receive-stock-receive">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setStep('setup')} data-testid="rs-back-to-setup">
            <ArrowLeft className="w-4 h-4 mr-1" /> Edit setup
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              {setupForm.supplier_name}
            </h1>
            <p className="text-xs text-slate-500">
              {setupForm.invoice_number ? `Invoice #${setupForm.invoice_number} • ` : ''}
              {storeName(setupForm.store)} • Total {fmt(invoiceTotal)} (GST {fmt(enteredGst)}{freight > 0 ? ` • Freight ${fmt(freight)}` : ''}{ccSurcharge > 0 ? ` • CC ${fmt(ccSurcharge)}` : ''})
            </p>
          </div>
        </div>
      </div>

      <DraftBanner />

      {/* Scan + Search bar */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Scan barcode</Label>
              <div className="flex items-center gap-2">
                <Scan className="w-5 h-5 text-orange-500" />
                <Input
                  ref={scanRef}
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScan(scanInput); } }}
                  placeholder="Scan or type barcode then Enter"
                  className="text-base"
                  data-testid="rs-scan-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-slate-500">Search by name</Label>
              <div className="flex items-center gap-2">
                <Search className="w-5 h-5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type product name…"
                  data-testid="rs-search-input"
                />
              </div>
            </div>
            <Button
              type="button"
              onClick={() => setAddProductDialog({
                barcode: '',
                name: searchQuery || '',
                brand: '',
                supplier_id: setupForm.supplier_id,
                supplier_name: setupForm.supplier_name,
                supplier_code: '',
                tax_rate: 15,
                category: 'General',
                buy_price: '',
                sell_price: '',
                stock_qty: 1,
                publish_to_shopify: false,
                creating: false,
              })}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-10"
              data-testid="rs-new-product-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Product
            </Button>
          </div>

          {/* Search dropdown — searches ALL products (any supplier). Shows the
              current supplier's matches first, then cross-supplier matches with
              a small supplier-name hint so the user knows they're receiving
              stock from a product linked elsewhere. Fixes the "we buy the same
              product from multiple suppliers" workflow. */}
          {searchQuery.trim().length >= 2 && (() => {
            const q = searchQuery.toLowerCase();
            const all = products.filter(p =>
              (p.name || '').toLowerCase().includes(q) ||
              (p.barcode || '').includes(searchQuery)
            );
            // Current supplier first, cross-supplier next
            const own = all.filter(p => p.supplier_id === selectedSupplierId);
            const other = all.filter(p => p.supplier_id !== selectedSupplierId);
            const shown = [...own, ...other].slice(0, 20);
            return (
              <div className="border rounded-md max-h-56 overflow-y-auto bg-white" data-testid="rs-search-dropdown">
                {shown.map(p => {
                  const isCross = p.supplier_id !== selectedSupplierId;
                  return (
                    <button
                      key={p.id}
                      onClick={() => { addLineForProduct(p, 1); setSearchQuery(''); }}
                      className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-orange-50 flex justify-between items-center ${isCross ? 'bg-amber-50/60' : ''}`}
                      data-testid={`rs-search-result-${p.id}`}
                      title={isCross ? `This product is linked to ${p.supplier_name || 'another supplier'} in your catalogue — you can still receive it here` : ''}
                    >
                      <span className="flex-1">
                        <span className="font-medium">{p.name}</span>
                        {isCross && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
                            from {p.supplier_name || 'other supplier'}
                          </span>
                        )}
                      </span>
                      <span className="text-slate-400 text-xs ml-2">{p.barcode || '—'}</span>
                    </button>
                  );
                })}
                {shown.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-500 flex items-center justify-between">
                    <span>No matches for &quot;{searchQuery}&quot;.</span>
                    <button
                      onClick={() => setAddProductDialog({
                        barcode: '',
                        name: searchQuery,
                        brand: '',
                        supplier_id: setupForm.supplier_id,
                        supplier_name: setupForm.supplier_name,
                        supplier_code: '',
                        tax_rate: 15,
                        category: 'General',
                        buy_price: '',
                        sell_price: '',
                        stock_qty: 1,
                        publish_to_shopify: false,
                        creating: false,
                      })}
                      className="text-emerald-700 font-semibold hover:underline"
                      data-testid="rs-add-from-search"
                    >
                      + Create &quot;{searchQuery}&quot;
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Lines table */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h2 className="font-bold text-slate-700">Items received <Badge variant="outline" className="ml-2">{lines.length}</Badge></h2>
            <span className="text-xs text-slate-500">Net total: <b>{fmt(lineNet)}</b></span>
          </div>
          {lines.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Scan a barcode to add the first item</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2 px-3">Product</th>
                    <th className="text-right p-2 w-20">Qty</th>
                    <th className="text-right p-2 w-28">Cost (ex GST)</th>
                    <th className="text-right p-2 w-28">Retail</th>
                    <th className="text-center p-2 w-24">Margin</th>
                    <th className="text-right p-2 w-24">Line Net</th>
                    <th className="w-32" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map(l => {
                    const mp = marginPct(l.unit_cost, l.sell_price);
                    const underMarkup = l.unit_cost > 0 && l.sell_price + 0.005 < l.unit_cost * MIN_SELL_MULTIPLIER;
                    const violation = underMarkup && !l.markup_override;
                    return (
                      <tr key={l.key} className={`border-t ${violation ? 'bg-red-50' : 'hover:bg-slate-50'}`} data-testid={`rs-line-${l.product_id}`}>
                        <td className="p-2 px-3">
                          <div className="font-medium text-slate-800">{l.name}</div>
                          <div className="text-xs text-slate-400 flex items-center gap-2">
                            {l.barcode || '—'}
                            {l.markup_override && (
                              <Badge className="bg-amber-100 text-amber-800 text-[10px] py-0 px-1.5">
                                <ShieldAlert className="w-3 h-3 mr-0.5" /> Override: {l.override_reason}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number" min="1" step="1"
                            value={l.quantity}
                            onChange={(e) => updateLine(l.key, { quantity: parseInt(e.target.value) || 0 })}
                            className="text-right h-8"
                            data-testid={`rs-qty-${l.product_id}`}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number" step="0.01" min="0"
                            value={l.unit_cost}
                            onChange={(e) => updateLine(l.key, { unit_cost: parseFloat(e.target.value) || 0, markup_override: false })}
                            className="text-right h-8"
                            data-testid={`rs-cost-${l.product_id}`}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number" step="0.01" min="0"
                            value={l.sell_price}
                            onChange={(e) => updateLine(l.key, { sell_price: parseFloat(e.target.value) || 0, markup_override: false })}
                            className={`text-right h-8 ${violation ? 'border-red-300 text-red-700' : ''}`}
                            data-testid={`rs-retail-${l.product_id}`}
                          />
                        </td>
                        <td className="p-2 text-center">
                          <span className={`text-xs font-bold ${
                            l.unit_cost <= 0 ? 'text-slate-400' :
                            mp >= 42 ? 'text-emerald-600' :
                            l.markup_override ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {l.sell_price > 0 ? `${mp.toFixed(0)}%` : '—'}
                          </span>
                          {l.sell_price > 0 && l.unit_cost > 0 && (
                            <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`rs-line-profit-${l.product_id}`}>
                              ${(((l.sell_price / GST_MULT) - l.unit_cost) * l.quantity).toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right font-medium">{fmt(l.quantity * l.unit_cost)}</td>
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {violation && (
                              <Button
                                size="sm"
                                className="h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold"
                                onClick={() => openOverrideDialog(l.key)}
                                data-testid={`rs-override-inline-${l.product_id}`}
                                title="This line is below 42% margin — enter manager PIN + reason to accept"
                              >
                                <ShieldAlert className="w-3.5 h-3.5 mr-0.5" /> Override
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeLine(l.key)} data-testid={`rs-remove-${l.product_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Markup violations alert — WARNING only, no longer blocks Accept.
          You can still click Manager Override for per-line audit reasons, but
          hitting Accept will trigger a single confirm-anyway prompt. */}
      {linesUnderMarkup.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 font-bold">
              <AlertTriangle className="w-5 h-5" />
              {linesUnderMarkup.length} line(s) under 42% margin — you can still Accept, but review below.
            </div>
            <div className="space-y-1">
              {linesUnderMarkup.map(l => {
                const profit = ((l.sell_price / GST_MULT) - l.unit_cost) * l.quantity;
                return (
                  <div key={l.key} className="flex items-center justify-between text-sm bg-white border border-amber-200 rounded p-2">
                    <span>
                      <b>{l.name}</b> — retail {fmt(l.sell_price)} = only <b>${profit.toFixed(2)}</b> profit ({marginPct(l.unit_cost, l.sell_price).toFixed(0)}%) · fix to <b>{fmt(l.unit_cost * MIN_SELL_MULTIPLIER)}</b> for 42%
                    </span>
                    <Button size="sm" className="bg-amber-500 hover:bg-amber-600 h-7" onClick={() => openOverrideDialog(l.key)} data-testid={`rs-override-btn-${l.product_id}`}>
                      <ShieldAlert className="w-4 h-4 mr-1" /> Log override
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sticky balance bar + Accept */}
      <Card className={`sticky bottom-2 ${balanced ? 'border-emerald-300 bg-emerald-50' : remaining < -VARIANCE_TOLERANCE ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
        <CardContent className="p-4 space-y-3">
          {/* Running breakdown — builds up as you scan */}
          <div className="flex flex-wrap items-end gap-x-6 gap-y-2 text-sm">
            <div>
              <p className="text-[10px] uppercase text-slate-500">Items scanned</p>
              <p className="font-bold text-base">{fmt(lineNet)}</p>
            </div>
            <span className="text-slate-400 pb-1">+</span>
            <div>
              <p className="text-[10px] uppercase text-slate-500">GST</p>
              <p className="font-medium text-base">{fmt(enteredGst)}</p>
            </div>
            {freight > 0 && (
              <>
                <span className="text-slate-400 pb-1">+</span>
                <div>
                  <p className="text-[10px] uppercase text-slate-500">Freight</p>
                  <p className="font-medium text-base">{fmt(freight)}</p>
                </div>
              </>
            )}
            {ccSurcharge > 0 && (
              <>
                <span className="text-slate-400 pb-1">+</span>
                <div>
                  <p className="text-[10px] uppercase text-slate-500">CC Surcharge</p>
                  <p className="font-medium text-base">{fmt(ccSurcharge)}</p>
                </div>
              </>
            )}
            <span className="text-slate-400 pb-1">=</span>
            <div>
              <p className="text-[10px] uppercase text-slate-500">Running total</p>
              <p className="font-bold text-xl text-slate-900">{fmt(runningTotal)}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-[10px] uppercase text-slate-500">Invoice target</p>
              <p className="font-bold text-xl text-slate-700">{fmt(invoiceTotal)}</p>
            </div>
          </div>

          {/* Status row + Accept */}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-2 border-t">
            <div className="text-sm font-semibold">
              {balanced ? (
                <span className="text-emerald-700 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" /> Balanced — ready to accept
                </span>
              ) : remaining > VARIANCE_TOLERANCE ? (
                <span className="text-amber-700">
                  Add <b>{fmt(remaining)}</b> more to balance
                </span>
              ) : (
                <span className="text-red-700">
                  Over by <b>{fmt(Math.abs(remaining))}</b> — remove or adjust a line
                </span>
              )}
              <span className="text-[11px] text-slate-400 ml-2">tolerance ±$0.50</span>
              {overByExactlyGst && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-sm font-normal">
                  <p className="text-amber-800 text-sm">
                    You're over by <b>exactly the GST</b> — your line costs probably already <b>include GST</b>.
                    The Cost column must be ex-GST.
                  </p>
                  <Button
                    size="sm"
                    onClick={convertLinesToExGst}
                    className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="rs-convert-lines-ex-gst"
                  >
                    Convert lines to ex-GST (÷1.15)
                  </Button>
                </div>
              )}
            </div>
            <Button
              disabled={!canAccept}
              onClick={handleAccept}
              className={`h-12 px-6 text-base font-semibold ${canAccept ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              data-testid="rs-accept-button"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
              Accept & Update Inventory
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Override dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={(open) => !open && setOverrideDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600" /> Manager Override</DialogTitle>
            <DialogDescription>
              Retail price is below 42% margin. Manager PIN + reason required. This will be logged under <b>Staff Activity</b>.
            </DialogDescription>
          </DialogHeader>
          {overrideDialog && (
            <div className="space-y-3">
              <div>
                <Label>Manager PIN *</Label>
                <Input
                  type="password" autoFocus
                  value={overrideDialog.pin}
                  onChange={(e) => setOverrideDialog({ ...overrideDialog, pin: e.target.value })}
                  placeholder="Enter PIN"
                  onKeyDown={(e) => { if (e.key === 'Enter') submitOverride(); }}
                  data-testid="rs-override-pin"
                />
              </div>
              <div>
                <Label>Reason *</Label>
                <Input
                  value={overrideDialog.reason}
                  onChange={(e) => setOverrideDialog({ ...overrideDialog, reason: e.target.value })}
                  placeholder="e.g. Promo pricing, clearance, supplier discount"
                  data-testid="rs-override-reason"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialog(null)}>Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={submitOverride}
              disabled={!overrideDialog?.pin || !overrideDialog?.reason || overrideDialog?.verifying}
              data-testid="rs-override-submit"
            >
              {overrideDialog?.verifying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldAlert className="w-4 h-4 mr-1" />}
              Approve Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Add Product dialog (unknown barcode OR manual "New Product") */}
      <Dialog open={!!addProductDialog} onOpenChange={(open) => !open && setAddProductDialog(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Add New Product</DialogTitle>
            <DialogDescription>
              Quickly create a product while receiving stock — supplier is locked to{' '}
              <b>{addProductDialog?.supplier_name || 'this PO'}</b>.
            </DialogDescription>
          </DialogHeader>
          {addProductDialog && (
            <div className="space-y-3">
              <div>
                <Label>Product Name *</Label>
                <Input
                  autoFocus
                  value={addProductDialog.name}
                  onChange={(e) => setAddProductDialog({ ...addProductDialog, name: e.target.value })}
                  placeholder="e.g. Solgar Vitamin D3 1000IU"
                  data-testid="rs-add-name"
                />
              </div>

              {/* Barcode + Supplier Code */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Barcode</Label>
                  <Input
                    value={addProductDialog.barcode}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, barcode: e.target.value })}
                    placeholder="Scan or type"
                    data-testid="rs-add-barcode"
                  />
                </div>
                <div>
                  <Label>Supplier Code</Label>
                  <Input
                    value={addProductDialog.supplier_code}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, supplier_code: e.target.value })}
                    placeholder="e.g. ABC-12345"
                    data-testid="rs-add-supplier-code"
                  />
                </div>
              </div>

              {/* Brand + Category */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Brand</Label>
                  <Input
                    value={addProductDialog.brand}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, brand: e.target.value })}
                    placeholder="Brand"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Input
                    value={addProductDialog.category}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, category: e.target.value })}
                  />
                </div>
              </div>

              {/* Pricing: Cost / Retail / Tax */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Cost (ex GST) *</Label>
                  <Input
                    type="number" step="0.01"
                    value={addProductDialog.buy_price}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, buy_price: e.target.value })}
                    data-testid="rs-add-cost"
                  />
                </div>
                <div>
                  <Label>Retail *</Label>
                  <Input
                    type="number" step="0.01"
                    value={addProductDialog.sell_price}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, sell_price: e.target.value })}
                    data-testid="rs-add-retail"
                  />
                  {addProductDialog.buy_price > 0 && addProductDialog.sell_price > 0 && (
                    <p className={`text-xs mt-1 ${marginPct(addProductDialog.buy_price, addProductDialog.sell_price) >= 42 ? 'text-emerald-600' : 'text-red-600'}`}>
                      Margin: {marginPct(addProductDialog.buy_price, addProductDialog.sell_price).toFixed(0)}%
                    </p>
                  )}
                </div>
                <div>
                  <Label>Tax (%)</Label>
                  <Input
                    type="number" step="0.1"
                    value={addProductDialog.tax_rate}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, tax_rate: e.target.value })}
                    data-testid="rs-add-tax"
                  />
                </div>
              </div>

              <div>
                <Label>Quantity received *</Label>
                <Input
                  type="number" min="1" step="1"
                  value={addProductDialog.stock_qty}
                  onChange={(e) => setAddProductDialog({ ...addProductDialog, stock_qty: e.target.value })}
                  data-testid="rs-add-qty"
                />
              </div>

              {/* Publish to Shopify */}
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!addProductDialog.publish_to_shopify}
                    onChange={(e) => setAddProductDialog({ ...addProductDialog, publish_to_shopify: e.target.checked })}
                    className="w-5 h-5 rounded border-green-400 text-green-600 focus:ring-green-500"
                    data-testid="rs-add-publish-shopify"
                  />
                  <div>
                    <span className="font-medium text-green-800">Publish to Shopify</span>
                    <p className="text-xs text-green-600">Push this product to your Shopify store after creating it.</p>
                  </div>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddProductDialog(null)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600"
              onClick={handleQuickAddProduct}
              disabled={addProductDialog?.creating}
              data-testid="rs-add-product-submit"
            >
              {addProductDialog?.creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Add Product & Receive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiveStock;
